/* eslint-disable react/display-name */

import { Flex, Text } from '@chakra-ui/react';

import { ReactComponent as PenIcon } from 'assets/svgs/pencil-line.svg';
import { Button } from 'components';
import PaymentMethodEdit from 'components/PaymentMethod/PaymentMethodEdit';
import PaymentMethodView from 'components/PaymentMethod/PaymentMethodView';

import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled/macro';
import { mq } from 'styles/breakpoints';
import { appContextSelector, isCorporateThemeSelector } from 'modules/app/appSlice';
import { RootState } from 'store';
import { isPaymentMethodInvalid } from 'components/PaymentMethod/utils';

const SectionBox = styled(Flex)`
  flex-direction: column;
  padding: 30px 20px;

  ${mq.md} {
    padding: 0;
    border-radius: 0;
    background: transparent;
  }
`;

interface Props {
  buttonAligment?: 'left' | 'right';
  onSave?: () => void;
  onCancel?: () => void;
  onSkip?: () => void;
  startInEditMode?: boolean;
  isModal?: boolean;
}

const CardDetails: FC<Props> = ({ buttonAligment, onSave, onCancel, onSkip, startInEditMode, isModal }) => {
  const [isEdit, setIsEdit] = useState(startInEditMode ?? false);
  const { t } = useTranslation('paymentMethod');
  const { paymentMethods } = useSelector((state: RootState) => state.payments);
  const context = useSelector(appContextSelector);
  const cardPaymentMethod = paymentMethods?.find(({ type }) => type === 'Stripe');
  const isCardPaymentMethodInvalid = isPaymentMethodInvalid({ paymentMethod: cardPaymentMethod, context, country: 'true' });
  const isCorporateTheme = useSelector(isCorporateThemeSelector);

  return (
    <SectionBox
      mt={{ base: '15px', md: '45px' }}
      bg="white"
      sx={{ [mq.md]: { bg: 'transparent' }, '&:first-of-type': { mt: 0 } }}
      borderRadius="base"
    >
      <Flex justifyContent="space-between" align={{ base: 'center', xl: 'flex-start' }}>
        <Text as="h3" layerStyle="account.cardDetails.header" color="secondary.800" fontWeight={isCorporateTheme ? 500 : 400}>
          {t('cardDetails')}
        </Text>
        {(isModal || !isCardPaymentMethodInvalid) && !isEdit && (
          <Button
            variant={'outline'}
            colorScheme="gray"
            leftIcon={<PenIcon />}
            onClick={(): void => setIsEdit(true)}
            color="secondary.800"
            borderColor="secondary.800"
            sx={{
              marginBottom: isCorporateTheme ? { base: '10px', xl: 0 } : 0,
              '& > span > svg': {
                marginRight: isCorporateTheme ? { base: '10px', sm: '10px' } : 0,
              },
            }}
          >
            <span>{t('editBtn')}</span>
          </Button>
        )}
      </Flex>
      {isEdit ? (
        <PaymentMethodEdit
          buttonAligment={buttonAligment}
          onSkip={onSkip}
          onCancel={(): void => {
            !isModal && setIsEdit(false);
            onCancel && onCancel();
          }}
          onSave={(): void => {
            onSave && onSave();
            setIsEdit(false);
          }}
        />
      ) : (
        <PaymentMethodView onClick={(): void => setIsEdit(true)} isModal={isModal} />
      )}
    </SectionBox>
  );
};

export default CardDetails;
